import affirm from '../core/Affirm'
import { ICMAutocomplete } from '../definitions/MiscSchema'
import { IFlow, INode, INodeReportPlaceholder, JsonSchema, NodeType } from '../definitions/Nodes'
import { ALL_NODES_DESCRIPTION, INodeDescription } from '../definitions/NodesDescription'
import { IReportPlaceholder } from '../definitions/report_builder/ReportBuilderDefinitions'

export interface IShapeInput {
    nodeUuid: string
    nodeName: string
    type: NodeType
    argKey: string
    outKey: string
    shape: JsonSchema
}

class NodeManagerClass {
    getDescription = <T extends NodeType>(type: T): INodeDescription<T> => {
        affirm(type, 'Invalid @type parameter')
        return ALL_NODES_DESCRIPTION.find(x => x.type === type) as any as INodeDescription<T>
    }

    getDescriptions = (): INodeDescription<NodeType>[] => {
        return ALL_NODES_DESCRIPTION as INodeDescription<NodeType>[]
    }

    getListInputs = (flow: IFlow, selectedNode: INode<NodeType>) => {
        const results: ICMAutocomplete[] = []

        if (Object.keys(flow.globals).length === 0) return results
        const globalKeys = Object.keys(flow.globals)

        for (const globalKey of globalKeys) {
            results.push({
                label: 'globals' + '.' + globalKey,
                type: 'globals',
                value: 'globals' + '.' + globalKey
            })
        }

        if (selectedNode.inputs.length === 0) return results

        for (const input of selectedNode.inputs) {
            const inputNode = flow.nodes.find(k => k.uuid === input.uuid)
            affirm(inputNode, 'The selected node has as input a node that does not exist in the flow')
            const schemaKeys = inputNode?.jsonSchema?.items?.properties
                ? Object.keys(inputNode.jsonSchema.items.properties)
                : inputNode?.jsonSchema?.properties
                    ? Object.keys(inputNode.jsonSchema.properties)
                    : null
            if (!schemaKeys) continue
            for (const schemaKey of schemaKeys) {
                results.push({
                    label: inputNode.name + '.' + input.outputKey + '.' + schemaKey,
                    type: inputNode.type,
                    value: input.uuid + '.' + input.outputKey + '.' + schemaKey
                })
            }
        }

        return results
    }

    getInputShapes = <T extends NodeType>(node: INode<T>, nodes: INode[]): IShapeInput[] => (
        node.inputs.map(ni => {
            const { uuid, name, jsonSchema, type } = nodes.find(n => n.uuid === ni.uuid)
            return { nodeUuid: uuid, nodeName: name, type, outKey: ni.outputKey, argKey: ni.argumentKey as string, shape: jsonSchema }
        })
    )

    findShape = (shapes: IShapeInput[], sourceHandle: string) => {
        return shapes == null ? undefined : shapes.find(s => s.argKey === sourceHandle)
    }

    getMappedShapes = (matchedShape: IShapeInput): string[] => {
        if (!matchedShape?.shape) return []

        const collectedPaths: string[] = []

        const traverseSchema = (schema: JsonSchema, currentPath = ''): void => {
            const addPath = (key: string) => {
                const newPath = currentPath ? `${currentPath}.${key}` : key
                collectedPaths.push(newPath)
                return newPath
            }

            if (schema.type === 'array' && schema.items?.type === 'object') {
                for (const key of Object.keys(schema.items.properties)) {
                    traverseSchema(schema.items.properties[key], addPath(key))
                }
                return
            }

            if (schema.type === 'object' && schema.properties) {
                for (const key of Object.keys(schema.properties)) {
                    traverseSchema(schema.properties[key], addPath(key))
                }
            }
        }

        traverseSchema(matchedShape.shape)
        return collectedPaths
    }

    mapReport = (placeholders: IReportPlaceholder[], nodePlaceholders: INodeReportPlaceholder[]): { placeholders: INodeReportPlaceholder[], contents: { name: string, contents: string[] }[] } => {
        const contents: { name: string, contents: string[] }[] = []
        return {
            placeholders: placeholders?.length
                ? placeholders.map(placeholder => {
                    const selectedPlaceholder = nodePlaceholders?.find(p => p.name === placeholder.name) ?? null
                    contents.push({ name: placeholder.name, contents: placeholder.contents })
                    return {
                        name: selectedPlaceholder?.name ?? placeholder.name,
                        referenceData: selectedPlaceholder?.referenceData ?? '',
                        columns: placeholder?.columns?.length
                            ? placeholder.columns.map(column => {
                                const selectedColumn = selectedPlaceholder?.columns?.find(c => c.name === column) ?? null
                                return { name: selectedColumn?.name ?? column, value: selectedColumn?.value ?? '' }
                            })
                            : null,
                        ...(selectedPlaceholder?.condition && { condition: selectedPlaceholder.condition })
                    }
                })
                : [],
            contents
        }
    }
}

const NodeManager = new NodeManagerClass()
export default NodeManager
